// Org context. Exposes:
//   - orgId: the user's current org (from their profile), or null
//   - role: 'admin' | 'member' | null — the caller's role in this org
//
// Both are kept live via Firestore listeners so role transitions and org
// switches reflect immediately across the app. Components that need to
// gate UI on admin status (Team-Settings link, RequireAdmin guard, etc.)
// should depend on this rather than running their own snapshot — fewer
// listeners, single source of truth.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { orgMemberDoc } from '../lib/firestore';
import type { Role } from '../lib/types';

type OrgContextValue = {
  orgId: string | null;
  /** True while either auth/profile or the role lookup is in flight. */
  loading: boolean;
  /** Caller's role in the current org. null = not in an org, or member doc missing. */
  role: Role | null;
};

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user, profile, loading: authLoading } = useAuth();
  const orgId = profile?.orgId ?? null;

  const [role, setRole] = useState<Role | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    if (!user || !orgId) {
      setRole(null);
      setRoleLoading(false);
      return;
    }
    setRoleLoading(true);
    const unsub = onSnapshot(
      orgMemberDoc(orgId, user.uid),
      (snap) => {
        if (!snap.exists()) {
          setRole(null);
        } else {
          const data = snap.data() as { role?: string };
          setRole(data.role === 'admin' ? 'admin' : 'member');
        }
        setRoleLoading(false);
      },
      (err) => {
        console.error('[org] member-role snapshot failed', err);
        setRole(null);
        setRoleLoading(false);
      },
    );
    return () => unsub();
  }, [user, orgId]);

  const value = useMemo<OrgContextValue>(
    () => ({ orgId, loading: authLoading || roleLoading, role }),
    [orgId, authLoading, roleLoading, role],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
}
